import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Feedback } from '../types';
import './FeedbackDetail.css';

const FeedbackDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadFeedback = useCallback(async (): Promise<void> => {
    if (!id) return;
    try {
      const response = await api.get<Feedback>(`/feedbacks/${id}`);
      setFeedback(response.data);
    } catch (err: any) {
      setError('Erro ao carregar feedback');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadFeedback();
    }
  }, [id, loadFeedback]);

  const formatDate = useCallback(
    (value?: string): string =>
      value ? new Date(value).toLocaleDateString('pt-BR') : 'Não informado',
    [],
  );

  const formatAdherence = useCallback((value?: string): string => {
    if (!value) return 'Não informado';
    if (value === 'sim') return 'Sim';
    if (value === 'parcialmente') return 'Parcialmente';
    return 'Não';
  }, []);

  const summaryCards = useMemo(
    () =>
      feedback
        ? [
            {
              label: 'Score final',
              value: feedback.finalScore,
              description: 'Resultado consolidado do ciclo.',
            },
            {
              label: 'Ciclo',
              value: feedback.cycle?.name || 'Não definido',
              description: 'Período vinculado à avaliação.',
            },
            {
              label: 'Data',
              value: formatDate(feedback.createdAt),
              description: 'Registro oficial do feedback.',
            },
          ]
        : [],
    [feedback, formatDate],
  );

  if (loading) {
    return (
      <div className="detail-state">
        <div className="detail-state-card">
          <span className="detail-badge">Carregando</span>
          <h1>Preparando feedback</h1>
          <p>Organizando dados, métricas e consolidado da avaliação.</p>
        </div>
      </div>
    );
  }

  if (error || !feedback) {
    return (
      <div className="detail-state">
        <div className="detail-state-card">
          <span className="detail-badge">Indisponível</span>
          <h1>{error || 'Feedback não encontrado'}</h1>
          <button onClick={() => navigate('/feedbacks')} className="detail-action-button is-secondary">
            Voltar para feedbacks
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="detail-page">
      <section className="detail-hero">
        <div className="detail-hero-copy">
          <span className="detail-badge">Feedback técnico</span>
          <h1>Detalhes da avaliação</h1>
          <p>
            Visualize o contexto do ciclo, os critérios avaliados e o consolidado com pontos fortes,
            melhorias e próximos passos.
          </p>
        </div>

        <div className="detail-hero-actions">
          <button onClick={() => navigate('/feedbacks')} className="detail-action-button is-secondary">
            Voltar para feedbacks
          </button>
        </div>
      </section>

      <section className="detail-summary-grid">
        {summaryCards.map((card) => (
          <article className="detail-summary-card" key={card.label}>
            <span>{card.label}</span>
            <strong>{card.value}</strong>
            <p>{card.description}</p>
          </article>
        ))}
      </section>

      <section className="detail-surface">
        <div className="detail-section-header">
          <div>
            <span className="detail-section-kicker">Geral</span>
            <h2>Informações principais</h2>
          </div>
        </div>

        <div className="detail-info-grid">
          <div className="detail-info-card">
            <span>Desenvolvedor</span>
            <strong>{feedback.developer?.name || 'Não informado'}</strong>
          </div>
          <div className="detail-info-card">
            <span>Gestor</span>
            <strong>{feedback.manager?.name || 'Não informado'}</strong>
          </div>
          <div className="detail-info-card">
            <span>Ciclo</span>
            <strong>{feedback.cycle?.name || 'Não definido'}</strong>
          </div>
          <div className="detail-info-card">
            <span>Data</span>
            <strong>{formatDate(feedback.createdAt)}</strong>
          </div>
        </div>
      </section>

      <section className="detail-surface">
        <div className="detail-section-header">
          <div>
            <span className="detail-section-kicker">Produtividade</span>
            <h2>Produtividade técnica</h2>
          </div>
        </div>

        <div className="detail-metrics-grid">
          <div className="detail-metric-card">
            <span>Score</span>
            <strong>{feedback.productivityScore}/5</strong>
          </div>
          <div className="detail-metric-card">
            <span>Telas</span>
            <strong>{feedback.screensDeveloped}</strong>
          </div>
          <div className="detail-metric-card">
            <span>Tarefas</span>
            <strong>{feedback.tasksCompleted}</strong>
          </div>
          <div className="detail-metric-card">
            <span>Horas</span>
            <strong>{feedback.hoursWorked}</strong>
          </div>
          <div className="detail-metric-card">
            <span>Complexidade</span>
            <strong>{feedback.averageComplexity}/10</strong>
          </div>
        </div>

        <div className="detail-text-grid">
          <article className="detail-text-card">
            <h3>Justificativa</h3>
            <p>{feedback.productivityJustification}</p>
          </article>
          {feedback.productivityObservations && (
            <article className="detail-text-card">
              <h3>Observações</h3>
              <p>{feedback.productivityObservations}</p>
            </article>
          )}
        </div>
      </section>

      <section className="detail-content-grid">
        <article className="detail-surface">
          <div className="detail-section-header">
            <div>
              <span className="detail-section-kicker">Código</span>
              <h2>Qualidade do código</h2>
            </div>
          </div>

          <div className="detail-metrics-grid is-compact">
            <div className="detail-metric-card">
              <span>Score</span>
              <strong>{feedback.codeQualityScore}/5</strong>
            </div>
            <div className="detail-metric-card">
              <span>Aderência</span>
              <strong>{formatAdherence(feedback.codeAdherence)}</strong>
            </div>
          </div>

          {feedback.codeQualityObservations && (
            <article className="detail-text-card">
              <h3>Observações</h3>
              <p>{feedback.codeQualityObservations}</p>
            </article>
          )}
        </article>

        <article className="detail-surface">
          <div className="detail-section-header">
            <div>
              <span className="detail-section-kicker">Testes</span>
              <h2>Validação e testes</h2>
            </div>
          </div>

          <div className="detail-metrics-grid is-compact">
            <div className="detail-metric-card">
              <span>Score</span>
              <strong>{feedback.validationScore}/5</strong>
            </div>
            <div className="detail-metric-card">
              <span>Completude</span>
              <strong>{formatAdherence(feedback.validationCompleteness)}</strong>
            </div>
          </div>

          {feedback.validationObservations && (
            <article className="detail-text-card">
              <h3>Observações</h3>
              <p>{feedback.validationObservations}</p>
            </article>
          )}
        </article>
      </section>

      <section className="detail-surface">
        <div className="detail-section-header">
          <div>
            <span className="detail-section-kicker">Consolidado</span>
            <h2>Síntese do ciclo</h2>
          </div>
        </div>

        <div className="detail-text-grid detail-text-grid-compact">
          <article className="detail-text-card is-highlight">
            <h3>Pontos fortes</h3>
            <p>{feedback.strengths}</p>
          </article>
          <article className="detail-text-card is-highlight">
            <h3>Pontos de melhoria</h3>
            <p>{feedback.improvements}</p>
          </article>
          <article className="detail-text-card is-highlight">
            <h3>Ações para o próximo ciclo</h3>
            <p>{feedback.nextActions}</p>
          </article>
        </div>
      </section>
    </div>
  );
};

export default FeedbackDetail;
